import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, query, where, getDocs } from '@angular/fire/firestore';
import { Observable, BehaviorSubject, from, of } from 'rxjs';
import { map, switchMap, tap, catchError, shareReplay, take } from 'rxjs/operators';

export enum BookTitle {
  SHULCHAN_ARUKH = 'Shulchan_Arukh',
  TUR = 'Tur'
}

// Topic mapping for Firestore collections
export const TOPIC_MAPPING = {
  'kidushin': 'kidushin',
  'Israel-land-laws': 'Israel_land_laws'
};

export interface ChapterEntity {
  id?: string;
  bookTitle: BookTitle;
  bookSubTitle: string;
  chapter: number;
  firstSection: number;
  lastSection: number;
}

export interface ChapterIdentifier {
  bookTitle: BookTitle;
  bookSubTitle: string;
  chapter: number;
}

@Injectable({
  providedIn: 'root'
})
export class GenericCollectionService<T extends ChapterEntity> {


  private currentTopicSubject = new BehaviorSubject<string>('kidushin');
  public currentTopic$ = this.currentTopicSubject.asObservable();

  // Cache for entities, organized by bookTitle and bookSubTitle
  private entitiesMap = new Map<string, BehaviorSubject<T[]>>();

  // Track current active collection
  private activeCollectionSubject = new BehaviorSubject<string | null>(null);
  protected activeCollection$ = this.activeCollectionSubject.asObservable();

  constructor(private firestore: Firestore) { }

  // Load all documents from a single collection
  initializeAllCollections() {
    const topicId = this.getCurrentTopic();
    this.entitiesMap.clear(); // Clear previous entities
    return collectionData(collection(this.firestore, topicId), { idField: 'id' })
      .pipe(
        take(1),
        tap(allChapters => {
          // Group books by their collection key
          allChapters.forEach(chapter => {
            const collectionKey = this.getCollectionKey(chapter['bookTitle'], chapter['bookSubTitle']);

            if (!this.entitiesMap.has(collectionKey)) {
              this.entitiesMap.set(collectionKey, new BehaviorSubject<T[]>([]));
            }

            // Get current chapters for this collection
            const currentChapters = this.entitiesMap.get(collectionKey)?.getValue() || [];

            // Check if the chapter already exists (assuming a unique 'id' property)
            const chapterExists = currentChapters.some((existingChapter) => existingChapter.id === (chapter as any).id);

            if (!chapterExists) {
              // Add the new chapter and sort
              const updatedChapters = [...currentChapters, chapter as T].sort((a, b) => a.chapter - b.chapter);

              // Update the collection
              this.entitiesMap.get(collectionKey)?.next(updatedChapters);
            }
          });
        })
      );
  }

  getCollections(): Observable<T[]> {
    // Ensure collections are initialized first
    return this.initializeAllCollections().pipe(
      // After initialization, gather all chapters from all collections
      switchMap(() => {
        const allEntities: T[] = [];
        
        // Combine all entities from different collections
        this.entitiesMap.forEach((subject) => {
          const entities = subject.getValue();
          allEntities.push(...entities);
        });
        
        return of(allEntities);
      }),
      // Share the result so multiple subscribers don't trigger multiple initializations
      shareReplay(1),
      // Handle any errors that might occur
      catchError(error => {
        console.error('Error getting collections:', error);
        return of([]);
      })
    );
  }

  setCurrentTopic(topicId: string): void {
    this.currentTopicSubject.next(topicId);
    // Reload collections when topic changes
    this.initializeAllCollections().subscribe();
  }

  getFirestoreCollectionNameForExams(topicId: string): string {
    const TOPIC_MAPPING: { [key: string]: string } = {
      kidushin: 'kidushin',
      'Israel-land-laws': 'Israel_land_laws',
      'isurVeHeter': 'Issur_veHeter',
      'niddah': 'Niddah',
      'shabbat': 'shabbat'
    };
    return TOPIC_MAPPING[topicId] || 'kidushin'; // Default to kidushin if not found
  }

  getCurrentTopic(): string {
    return this.currentTopicSubject.value;
  }

  // Helper to get entities for a specific collection
  getEntities(bookTitle: BookTitle, bookSubTitle: string): Observable<T[]> {
    const collectionKey = this.getCollectionKey(bookTitle, bookSubTitle);
    return this.entitiesMap.get(collectionKey)?.asObservable() ?? of([]);
  }

  getRandomEntity(): Observable<T> {
    const allEntities: T[] = [];

    // Gather all entities from the entitiesMap
    this.entitiesMap.forEach((subject) => {
      const entities = subject.getValue(); // Assumes entities are stored in BehaviorSubject or ReplaySubject
      allEntities.push(...entities);
    });


    // Select a random entity
    const randomIndex = Math.floor(Math.random() * allEntities.length);
    const randomEntity = allEntities[randomIndex];

    return of(randomEntity) ?? of([]); // Wrap in an Observable
  }


  private getCollectionKey(bookTitle: BookTitle, bookSubTitle: string): string {
    return `${bookTitle},_${bookSubTitle}`;
  }

  private getFirestorePath(bookTitle: BookTitle, bookSubTitle: string): string {
    return `kidushin`;
  }

  // Set active collection
  setActiveCollection(bookTitle: BookTitle, bookSubTitle: string): Observable<boolean> {
    const collectionKey = this.getCollectionKey(bookTitle, bookSubTitle);

    return from(this.validateCollection(bookTitle, bookSubTitle)).pipe(
      tap(isValid => {
        if (isValid) {
          this.activeCollectionSubject.next(collectionKey);
        }
      })
    );
  }

  // Validate collection exists
  private async validateCollection(bookTitle: BookTitle, bookSubTitle: string): Promise<boolean> {
    const path = this.getFirestorePath(bookTitle, bookSubTitle);
    try {
      const querySnapshot = await getDocs(collection(this.firestore, path));
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error validating collection:', error);
      return false;
    }
  }

  // Navigation methods within a collection
  getNextChapter(current: ChapterEntity): Observable<{ entity: T; initialSection: number } | null> {
    const collectionKey = this.getCollectionKey(current.bookTitle, current.bookSubTitle);
    return this.entitiesMap.get(collectionKey)?.pipe(
      map(entities => {
        const sortedChapters = [...entities].sort((a, b) => a.chapter - b.chapter);
        const currentIndex = sortedChapters.findIndex(e => e.chapter === current.chapter);

        if (currentIndex < sortedChapters.length - 1) {
          const nextChapter = sortedChapters[currentIndex + 1];
          return {
            entity: nextChapter,
            initialSection: nextChapter.firstSection
          };
        }
        return null;
      })
    ) ?? of(null);
  }

  getAllChapters(): IterableIterator<string> {
    return this.entitiesMap.keys();
  }

  getAllEntitiesByBook(book: string) {
    return this.entitiesMap.get(book)?.getValue() || [];
  }

  getPreviousChapter(current: ChapterEntity): Observable<{ entity: T; initialSection: number } | null> {
    const collectionKey = this.getCollectionKey(current.bookTitle, current.bookSubTitle);
    return this.entitiesMap.get(collectionKey)?.pipe(
      map(entities => {
        const sortedChapters = [...entities].sort((a, b) => a.chapter - b.chapter);
        const currentIndex = sortedChapters.findIndex(e => e.chapter === current.chapter);

        if (currentIndex > 0) {
          const prevChapter = sortedChapters[currentIndex - 1];
          return {
            entity: prevChapter,
            initialSection: prevChapter.lastSection
          };
        }
        return null;
      })
    ) ?? of(null);
  }

  getNavigationLimits(identifier: ChapterIdentifier): Observable<{
    minSection: number;
    maxSection: number;
    hasPreviousChapter: boolean;
    hasNextChapter: boolean;
  }> {
    const collectionKey = this.getCollectionKey(identifier.bookTitle, identifier.bookSubTitle);
    return this.entitiesMap.get(collectionKey)?.pipe(
      map(entities => {
        const currentEntity = entities.find(e => e.chapter === identifier.chapter);
        if (!currentEntity) {
          throw new Error('Chapter not found');
        }

        const sortedChapters = [...entities].sort((a, b) => a.chapter - b.chapter);
        const currentIndex = sortedChapters.findIndex(e => e.chapter === identifier.chapter);

        return {
          minSection: currentEntity.firstSection,
          maxSection: currentEntity.lastSection,
          hasPreviousChapter: currentIndex > 0,
          hasNextChapter: currentIndex < sortedChapters.length - 1
        };
      })
    ) ?? of({
      minSection: 1,
      maxSection: 1,
      hasPreviousChapter: false,
      hasNextChapter: false
    });
  }
}